import React, { useState, useEffect, useRef } from 'react';
import { documentTypesAPI } from '../../services/api';
import { showToast } from '../common/Toast';

import {
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const initialForm = {
  label:      '',
  fee_amount: ''
};

const DocumentTypeModal = ({ open, onClose, onAddType }) => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [types, setTypes] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(initialForm);
  const [modalError, setModalError] = useState('');

  // Prevent double-fetch in React 18 Strict Mode (dev) per modal open
  const didFetch = useRef(false);
  // Memoize fetchTypes to avoid new function on every render
  const fetchTypes = React.useCallback(async () => {
    try {
      const { data } = await documentTypesAPI.getAllV1();
      setTypes(data.data);
    } catch {
      setModalError('Failed to load document types.');
    }
  }, []);

  useEffect(() => {
    if (open) {
      if (didFetch.current) return;
      didFetch.current = true;
      fetchTypes();
      setForm(initialForm);
      setError('');
      setModalError('');
      setEditId(null);
    } else {
      didFetch.current = false; // Reset when modal closes so next open will fetch again
    }
  }, [open, fetchTypes]);



  // Auto-generate key from label (frontend only, not sent to backend)
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      // Generate key from label (same as preview)
      const key = form.label
        ? form.label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
        : '';
      const payload = {
        label:      form.label,
        key:        key,
        fee_amount: parseFloat(form.fee_amount)
      };
      const { data } = await documentTypesAPI.create(payload);
      showToast('Document type added!', 'success');
      setForm(initialForm);
      if (onAddType) onAddType(data.data);
      if (open) fetchTypes();
    } catch (err) {
      setError('Failed to add document type.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (type) => {
    setEditId(type.id);
    setEditForm({ key: type.key, label: type.label, fee_amount: type.fee_amount });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setModalError('');
    try {
      await documentTypesAPI.update(editId, {
        label: editForm.label,
        fee_amount: parseFloat(editForm.fee_amount)
      });
      showToast('Document type updated!', 'success');
      setEditId(null);
      if (open) fetchTypes();
    } catch {
      setModalError('Failed to update document type.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document type?')) return;
    setLoading(true);
    setModalError('');
    try {
      await documentTypesAPI.delete(id);
      showToast('Document type deleted!', 'success');
      if (open) fetchTypes();
    } catch {
      setModalError('Failed to delete document type.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  // Ensure modal closes and resets state
  const handleModalClose = () => {
    setForm(initialForm);
    setError('');
    setModalError('');
    setEditId(null);
    if (onClose) onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl p-4 sm:p-6 relative border border-gray-200">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Manage Document Types</h3>
          <button
            type="button"
            onClick={handleModalClose}
            className="text-gray-400 hover:text-gray-600 text-2xl transition-all duration-300 transform hover:scale-125 hover:rotate-90 active:scale-95 focus:outline-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        {modalError && <div className="text-red-500 text-xs mb-2">{modalError}</div>}
        {/* Add Form */}
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2 items-end mb-4" autoComplete="off">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
            <input
              name="label"
              value={form.label}
              onChange={handleChange}
              className="block w-full border-2 border-gray-200 rounded-lg px-2 py-2 text-gray-900 placeholder-gray-400"
              required
              maxLength={100}
              placeholder="e.g. Barangay Clearance"
            />
            {/* Key is auto-generated and sent to backend, not shown to user */}
          </div>
          <div className="w-32">
            <label className="block text-sm font-medium text-gray-700 mb-1">Fee</label>
            <input
              name="fee_amount"
              type="number"
              min="0"
              step="0.01"
              value={form.fee_amount}
              onChange={handleChange}
              className="block w-full border-2 border-gray-200 rounded-lg px-2 py-2 text-gray-900 placeholder-gray-400"
              required
              placeholder="e.g. 50.00"
            />
          </div>
          <button
            type="button"
            onClick={handleSubmit}
            className="h-10 px-5 py-2 bg-blue-600 text-white rounded-lg font-semibold shadow hover:bg-blue-700 transition disabled:opacity-50"
            disabled={loading || !form.label || !form.fee_amount}
          >
            {loading ? 'Saving' : 'Add'}
          </button>
        </form>
        {error && <div className="text-red-500 text-xs mb-2">{error}</div>}
        {/* List of Types */}
        <div className="overflow-x-auto rounded border border-gray-100 bg-gray-50">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 border">Label</th>
                <th className="p-2 border">Fee</th>
                <th className="p-2 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {types.map((type) => (
                <tr key={type.id} className="bg-white even:bg-gray-50">
                  <td className="p-2 border">{
                    editId === type.id ? (
                      <input
                        name="label"
                        value={editForm.label}
                        onChange={handleEditChange}
                        className="border rounded px-3 py-2 text-sm w-full"
                      />
                    ) : type.label
                  }</td>
                  <td className="p-2 border text-right">{
                    editId === type.id ? (
                      <input
                        name="fee_amount"
                        type="number"
                        min="0"
                        step="1"
                        value={editForm.fee_amount}
                        onChange={handleEditChange}
                        className="border rounded px-3 py-2 text-sm w-24 text-right"
                      />
                    ) : `${parseFloat(type.fee_amount).toFixed(2)}`
                  }</td>
                  <td className="p-2 border text-center">
                    {editId === type.id ? (
                      <>
                        <button
                          onClick={handleEditSubmit}
                          className="text-green-600 hover:underline mr-2 text-xs"
                          disabled={loading}
                        >
                            <CheckIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditId(null)}
                          className="text-gray-500 hover:underline text-xs"
                          disabled={loading}
                        >
                            <XMarkIcon className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleEdit(type)}
                          className="text-blue-600 hover:underline mr-2 text-xs"
                          disabled={loading}
                        >
                            <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(type.id)}
                          className="text-red-600 hover:underline text-xs"
                          disabled={loading}
                        >
                            <TrashIcon className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {types.length === 0 && (
                <tr><td colSpan={3} className="p-2 text-center text-gray-400">No document types found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default React.memo(DocumentTypeModal);
